"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Calendar, BarChart3, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";


// Studio types
type StudioType = "creator" | "publisher" | "analytics";

interface StudioData {
  id: StudioType;
  label: string;
  icon: typeof Sparkles;
  screenshots: string[];
}

// Base64 blur placeholder
const BLUR_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAGCAYAAAD68A/GAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAIklEQVR4nGNgGPGAmZGR4T8DA8N/BgYGBmZGRob/yGwGXAAAVFwD/x5QLREAAAAASUVORK5CYII=";

const STUDIOS: StudioData[] = [
  {
    id: "creator",
    label: "Create",
    icon: Sparkles,
    screenshots: [
      "/screenshots/content-drawer-video.png",
      "/screenshots/content-grid.png",
      "/screenshots/files-grid.png",
    ],
  },
  {
    id: "publisher",
    label: "Publish",
    icon: Calendar,
    screenshots: [
      "/screenshots/calendar-with-event.png",
      "/screenshots/calendar-expanded.png",
      "/screenshots/blog-list.png",
    ],
  },
  {
    id: "analytics",
    label: "Track",
    icon: BarChart3,
    screenshots: [
      "/screenshots/dashboard-full.png",
      "/screenshots/repost-list.png",
    ],
  },
];

export function AnalyticsPreview() {
  const [activeStudio, setActiveStudio] = useState<StudioType>("creator");
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const currentStudio = STUDIOS.find((s) => s.id === activeStudio)!;
  const totalScreenshots = currentStudio.screenshots.length;

  const handleStudioChange = (id: StudioType) => {
    setActiveStudio(id);
    setActiveScreenshot(0);
  };

  const goToSlide = useCallback((index: number) => {
    if (carouselRef.current) {
      const slideWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({ left: slideWidth * index, behavior: "smooth" });
    }
    setActiveScreenshot(index);
  }, []);

  const goNext = useCallback(() => {
    const next = (activeScreenshot + 1) % totalScreenshots;
    goToSlide(next);
  }, [activeScreenshot, totalScreenshots, goToSlide]);

  const goPrev = useCallback(() => {
    const prev = activeScreenshot === 0 ? totalScreenshots - 1 : activeScreenshot - 1;
    goToSlide(prev);
  }, [activeScreenshot, totalScreenshots, goToSlide]);

  // Handle scroll snap detection
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      const slideWidth = carousel.offsetWidth;
      const newIndex = Math.round(carousel.scrollLeft / slideWidth);
      if (newIndex !== activeScreenshot && newIndex >= 0 && newIndex < totalScreenshots) {
        setActiveScreenshot(newIndex);
      }
    };

    carousel.addEventListener("scrollend", handleScroll);
    return () => carousel.removeEventListener("scrollend", handleScroll);
  }, [activeScreenshot, totalScreenshots]);

  // Reset carousel position when studio changes
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0, behavior: "instant" });
    }
  }, [activeStudio]);

  return (
    <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 10vh, 8rem)', paddingBottom: 'clamp(4rem, 10vh, 8rem)' }}>
      {/* Background gradient */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[800px] sm:w-[1000px] md:w-[1200px] h-[500px] sm:h-[600px]"
          style={{
            background: `
              radial-gradient(ellipse 60% 50% at 30% 50%, rgba(16, 185, 129, 0.06) 0%, transparent 60%),
              radial-gradient(ellipse 40% 40% at 70% 60%, rgba(163, 230, 53, 0.03) 0%, transparent 50%)
            `,
            filter: "blur(80px)",
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header - Left aligned, matching why-us section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-6 sm:mb-8"
        >
          <span className="text-accent text-sm font-medium uppercase tracking-wider mb-4 block">
            Toolkit
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            Your complete <span className="text-gradient">social toolkit</span>
          </h2>
          <p className="text-foreground-secondary text-sm md:text-base leading-relaxed max-w-md">
            Create, publish, and track — all in one place.
          </p>
        </motion.div>

        {/* Tabs - Always centered */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-6 sm:mb-8"
        >
          <div className="inline-flex items-center gap-0.5 p-1 rounded-full bg-card/50 border border-foreground/10 backdrop-blur-sm">
            {STUDIOS.map((studio) => {
              const isActive = activeStudio === studio.id;
              const Icon = studio.icon;

              return (
                <button
                  key={studio.id}
                  onClick={() => handleStudioChange(studio.id)}
                  className={cn(
                    "relative flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full transition-all duration-300",
                    "text-[11px] sm:text-xs font-medium",
                    isActive
                      ? "text-foreground"
                      : "text-foreground-muted hover:text-foreground-secondary"
                  )}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeStudioPill"
                      className="absolute inset-0 rounded-full bg-accent/15 border border-accent/30"
                      transition={{ type: "spring", duration: 0.4, bounce: 0.2 }}
                    />
                  )}
                  <Icon className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5 relative z-10", isActive && "text-accent")} />
                  <span className="relative z-10">{studio.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Screenshot Carousel with Navigation */}
        <div className="relative group">
          {/* Option B: Subtle Glow Effect - Behind carousel */}
          <div
            className="absolute inset-0 -inset-x-4 sm:-inset-x-8 pointer-events-none z-0"
            style={{
              background: "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(16, 185, 129, 0.08) 0%, transparent 70%)",
              filter: "blur(40px)",
            }}
          />

          {/* Option A: Carousel with Peek of Adjacent Slides */}
          <div className="relative px-0 sm:px-8 md:px-12 lg:px-16 z-10">
            {/* Fade edges for peek effect */}
            <div className="hidden sm:block absolute left-0 top-0 bottom-0 w-8 md:w-12 lg:w-16 bg-gradient-to-r from-background to-transparent z-20 pointer-events-none" />
            <div className="hidden sm:block absolute right-0 top-0 bottom-0 w-8 md:w-12 lg:w-16 bg-gradient-to-l from-background to-transparent z-20 pointer-events-none" />

            {/* Carousel Container */}
            <div
              ref={carouselRef}
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {currentStudio.screenshots.map((src, index) => (
                <div
                  key={src}
                  className="flex-shrink-0 w-full sm:w-[85%] md:w-[88%] snap-center px-1 sm:px-2"
                >
                  <motion.div
                    initial={{ opacity: 0.6, scale: 0.95 }}
                    animate={{
                      opacity: activeScreenshot === index ? 1 : 0.4,
                      scale: activeScreenshot === index ? 1 : 0.95,
                    }}
                    transition={{ duration: 0.3 }}
                    className="relative rounded-xl sm:rounded-2xl overflow-hidden border border-foreground/10 shadow-2xl shadow-black/40 bg-neutral-900"
                  >
                    {/* Browser Chrome - Minimal */}
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-neutral-900/80 border-b border-foreground/5">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-foreground/20" />
                        <div className="w-2 h-2 rounded-full bg-foreground/20" />
                        <div className="w-2 h-2 rounded-full bg-foreground/20" />
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="w-24 sm:w-32 mx-auto h-4 rounded bg-foreground/5" />
                      </div>
                    </div>

                    {/* Screenshot */}
                    <div className="relative aspect-[16/9] max-h-[50vh] sm:max-h-[55vh] md:max-h-[60vh]">
                      <Image
                        src={src}
                        alt={`${currentStudio.label} studio - view ${index + 1}`}
                        fill
                        priority={index === 0}
                        placeholder="blur"
                        blurDataURL={BLUR_DATA_URL}
                        className="object-cover object-top"
                        sizes="(max-width: 768px) 100vw, 1100px"
                      />
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Option E + C: Full-width Progress Line with Navigation Arrows and Large Watermark Number */}
          {totalScreenshots > 1 && (
            <div className="relative mt-5 sm:mt-6 px-0 sm:px-8 md:px-12 lg:px-16">
              <div className="flex items-center gap-0">
                {/* Left Arrow */}
                <button
                  onClick={goPrev}
                  className={cn(
                    "flex items-center justify-center mr-3 sm:mr-4",
                    "w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10",
                    "rounded-full border border-foreground/30 toolkit-arrow",
                    "bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
                    "text-foreground/50 hover:text-foreground/80",
                    "transition-all duration-200"
                  )}
                  aria-label="Previous screenshot"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                {/* Animated Progress Line - Full width */}
                <div className="relative flex-1 h-[3px] bg-foreground/15 rounded-full overflow-hidden self-center toolkit-progress">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent to-lime-400 rounded-full"
                    initial={false}
                    animate={{
                      width: `${((activeScreenshot + 1) / totalScreenshots) * 100}%`,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                </div>

                {/* Right Arrow */}
                <button
                  onClick={goNext}
                  className={cn(
                    "flex items-center justify-center ml-3 sm:ml-4",
                    "w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10",
                    "rounded-full border border-foreground/30 toolkit-arrow",
                    "bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
                    "text-foreground/50 hover:text-foreground/80",
                    "transition-all duration-200"
                  )}
                  aria-label="Next screenshot"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                {/* Large Watermark Number - Image number, bottom aligned with line */}
                <div className="relative ml-4 sm:ml-6 md:ml-8">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={activeScreenshot}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-none tabular-nums select-none"
                      style={{ color: "var(--section-index-color)" }}
                    >
                      {String(activeScreenshot + 1).padStart(2, "0")}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
